/**
 * 用页面字体在本地画海报，导出 1200×1600 PNG。
 * 逻辑尺寸是 600×800 的两倍，避免手机上发糊。调用方把结果放进 img，微信才能长按保存。
 */
import type { SharePayload, ShareSite } from './types.ts'
import { drawQr } from './qr.ts'
import { wrapLines } from './text.ts'
import { safeShareUrl } from './channels.ts'

const WIDTH = 1200
const HEIGHT = 1600

function firstChar(value: string | undefined, fallback: string): string {
  const chars = Array.from((value || '').trim())
  return chars[0] || fallback
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image()
    if (/^https?:\/\//i.test(src)) {
      try {
        const target = new URL(src, window.location.href)
        if (target.origin !== window.location.origin) image.crossOrigin = 'anonymous'
      } catch {
        image.crossOrigin = 'anonymous'
      }
    }
    image.onload = () => resolve(image)
    image.onerror = () => resolve(null)
    image.src = src
  })
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function drawPoster(
  ctx: CanvasRenderingContext2D,
  payload: SharePayload,
  site: ShareSite,
  includeSecret: boolean,
  image: HTMLImageElement | null,
): void {
  const theme = site.theme
  const font = theme.fontFamily
  ctx.clearRect(0, 0, WIDTH, HEIGHT)
  ctx.fillStyle = theme.paper
  ctx.fillRect(0, 0, WIDTH, HEIGHT)

  ctx.strokeStyle = theme.accent
  ctx.lineWidth = 8
  roundedRect(ctx, 36, 36, WIDTH - 72, HEIGHT - 72, 28)
  ctx.stroke()
  ctx.lineWidth = 2
  roundedRect(ctx, 56, 56, WIDTH - 112, HEIGHT - 112, 20)
  ctx.stroke()

  const seal = includeSecret ? firstChar(payload.secretSeal, firstChar(payload.seal, '享')) : firstChar(payload.seal, '享')
  ctx.fillStyle = theme.accent
  ctx.beginPath()
  ctx.arc(1040, 150, 54, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#fffaf3'
  ctx.font = `600 48px ${font}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(seal, 1040, 152)

  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  ctx.fillStyle = theme.primary
  ctx.font = `600 42px ${font}`
  ctx.fillText(site.name, 96, 110)
  ctx.fillStyle = theme.muted
  ctx.font = `400 28px ${font}`
  ctx.fillText(site.slogan, 96, 168)

  let cursor = 250
  if (payload.badge?.trim()) {
    ctx.font = `500 26px ${font}`
    const label = payload.badge.trim()
    const badgeWidth = Math.min(ctx.measureText(label).width + 36, 480)
    ctx.fillStyle = theme.bg
    roundedRect(ctx, 96, cursor, badgeWidth, 48, 10)
    ctx.fill()
    ctx.fillStyle = theme.primary
    ctx.fillText(label, 114, cursor + 10)
    cursor += 72
  }

  ctx.fillStyle = theme.text
  ctx.font = `600 54px ${font}`
  const headlineLines = wrapLines(payload.headline, 980, (line) => ctx.measureText(line).width, 2)
  for (const line of headlineLines) {
    ctx.fillText(line, 96, cursor)
    cursor += 68
  }
  cursor += 12

  const bodyParts = [payload.text.trim()]
  if (includeSecret && payload.secret?.trim()) bodyParts.push(payload.secret.trim())
  const body = bodyParts.filter(Boolean).join('\n')
  ctx.font = `400 36px ${font}`
  ctx.fillStyle = theme.text
  const bodyLines = wrapLines(body, 980, (line) => ctx.measureText(line).width, image ? 6 : 10)
  for (const line of bodyLines) {
    ctx.fillText(line, 96, cursor)
    cursor += 52
  }

  if (image) {
    const boxTop = Math.min(cursor + 24, 980)
    const boxSize = 420
    const scale = Math.min(boxSize / image.width, boxSize / image.height, 1)
    const dw = image.width * scale
    const dh = image.height * scale
    const dx = 96 + (boxSize - dw) / 2
    const dy = boxTop + (boxSize - dh) / 2
    ctx.drawImage(image, dx, dy, dw, dh)
  }

  const url = safeShareUrl(payload.url) || payload.url
  ctx.font = `400 28px ${font}`
  ctx.fillStyle = theme.muted
  const footer = payload.footer?.trim() || '扫码打开'
  const footerLines = wrapLines(footer, 640, (line) => ctx.measureText(line).width, 2)
  let footerY = 1320
  for (const line of footerLines) {
    ctx.fillText(line, 96, footerY)
    footerY += 40
  }
  drawQr(ctx, url, 860, 1260, 240)
}

/** 生成海报 data URL。跨域图片污染画布时退回纯文字再导出。 */
export async function renderSharePoster(
  payload: SharePayload,
  site: ShareSite,
  includeSecret: boolean,
): Promise<string> {
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready
    } catch {
      // 字体没加载完时继续用已有字体绘制
    }
  }
  const image = payload.imageUrl ? await loadImage(payload.imageUrl) : null
  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = HEIGHT
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('无法创建画布')
  drawPoster(ctx, payload, site, includeSecret, image)
  try {
    return canvas.toDataURL('image/png')
  } catch {
    drawPoster(ctx, payload, site, includeSecret, null)
    return canvas.toDataURL('image/png')
  }
}
